import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from 'next-themes';
import UploadList from './UploadList.tsx';
import { createTuneUpload } from '../../services/tuneService.ts';
import { fileConverter } from '../../utils/fileConverter.js';
import { toastHelper } from '../../utils/toastHelper.js';
import { initialUploadContainer } from '../../constants/uploadConstants.js';
import { validateContainer } from '../../utils/uploadValidator.js';
import { Dayjs } from 'dayjs';
import TermsBanner from '../termsBanner/TermsBanner.tsx';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../auth/AuthContext';
import { saveUploadData, loadUploadData, clearUploadData } from '../../utils/uploadStorage';

interface UploadContainerData {
  id: number;
  title: string;
  description: string;
  tags: string[];
  category: string;
  playlistId: string | null;
  privacyStatus: string;
  license: string;
  madeForKids: string;
  image: File | null;
  audio: File | null;
  uploadDate: Dayjs | null;
  isScheduled: boolean;
  imageResize?: {
    enabled: boolean;
    horizontal_offset?: number;
    vertical_offset?: number;
  } | null;
  disableWatermark?: string;
}

interface UploadedTuneLink {
  video_title: string;
  youtube_link_watch: string;
  benchmark_ffmpeg_process_time: string;
  benchmark_ffmpeg_upload_time: string;
}

interface TuneUploadProps {
  onLogin: (response: any) => void;
}

function TuneUpload({ onLogin }: TuneUploadProps) {
  const { isAuthenticated } = useAuth();
  const { hasFeature } = usePermissions();
  const { theme } = useTheme();
  const canBulkUpload = hasFeature('bulk_upload');
  const canCropImage = hasFeature('crop_image');
  const canAutoResizeImage = hasFeature('auto_resize_image');
  const canDisableWatermark = hasFeature('watermark_optional');

  const googleButtonRef = useRef<HTMLDivElement>(null);
  const nextIdRef = useRef(2);
  const justLoggedInOnMount = useRef(sessionStorage.getItem('justLoggedIn') === 'true');
  const [uploadContainers, setUploadContainers] = useState<UploadContainerData[]>([
    { ...initialUploadContainer, id: 1, imageResize: { enabled: true, horizontal_offset: undefined, vertical_offset: undefined }, uploadDate: null, isScheduled: false, playlistId: '', madeForKids: 'no', disableWatermark: 'yes' }
  ]);
  const [errorsList, setErrorsList] = useState<any[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedTuneLinks, setUploadedTuneLinks] = useState<UploadedTuneLink[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [showPortraitWarningModal, setShowPortraitWarningModal] = useState(false);
  const [portraitImageIndices, setPortraitImageIndices] = useState<number[]>([]);

  const prevCountRef = useRef(uploadContainers.length);

  // Clear the justLoggedIn flag immediately when component mounts
  if (justLoggedInOnMount.current) {
    sessionStorage.removeItem('justLoggedIn');
  }

  // Function to restore data after login
  const restoreDataAfterLogin = async () => {
    const savedData = await loadUploadData();
    const hasMeaningfulData = savedData && savedData.length > 0 && 
      savedData.some(container => container.audio || container.image);
    
    if (hasMeaningfulData) {
      setUploadContainers(savedData);
      const maxId = Math.max(...savedData.map(c => c.id));
      nextIdRef.current = maxId + 1;
      await clearUploadData();
      toastHelper.newMessage('success', 'Successfully logged in', 'Your upload data has been restored.');
    } else {
      // Clear any empty containers from IndexedDB
      if (savedData && savedData.length > 0) {
        await clearUploadData();
      }
      // No toast if no meaningful data was restored
      // User's files stayed in the form (no page reload), so no restoration occurred
    }
  };

  // Restore data once on mount if user just logged in
  useEffect(() => {
    if (justLoggedInOnMount.current && isAuthenticated) {
      restoreDataAfterLogin();
    }
  }, []); // Empty deps - runs once on mount

  // Set watermark default based on permission when adding new containers
  useEffect(() => {
    setUploadContainers(prev => prev.map(c => {
      // Only update if disableWatermark is not set yet
      if (c.disableWatermark === undefined) {
        return {
          ...c,
          disableWatermark: canDisableWatermark ? 'yes' : 'no'
        };
      }
      return c;
    }));
  }, [canDisableWatermark]);

  useEffect(() => {
    if (uploadContainers.length <= prevCountRef.current) {
      prevCountRef.current = uploadContainers.length;
      return;
    }
    prevCountRef.current = uploadContainers.length;

    const newIndex = uploadContainers.length - 1;
    const timer = setTimeout(() => {
      const newTrack = document.querySelector(`[data-track-index="${newIndex}"]`);
      if (!newTrack) return;

      const elementRect = newTrack.getBoundingClientRect();
      const elementTop = elementRect.top + window.pageYOffset;
      const elementHeight = elementRect.height;
      const viewportHeight = window.innerHeight;
      const topOffset = 100;
      const bottomPadding = 40;

      let targetScroll: number;
      if (elementHeight > viewportHeight - topOffset - bottomPadding) {
        targetScroll = elementTop + elementHeight - viewportHeight + bottomPadding;
      } else {
        targetScroll = elementTop - topOffset;
      }
      targetScroll = Math.max(0, targetScroll);

      const startScroll = window.pageYOffset;
      const distance = targetScroll - startScroll;
      const duration = 800;
      let startTime: number | null = null;

      const easeInOutCubic = (t: number): number =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animateScroll = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        window.scrollTo({ top: startScroll + distance * easeInOutCubic(progress), behavior: 'auto' });
        if (progress < 1) requestAnimationFrame(animateScroll);
      };

      requestAnimationFrame(animateScroll);
    }, 200);

    return () => clearTimeout(timer);
  }, [uploadContainers.length]);

  const handleAddContainer = () => {
    if (!canBulkUpload) {
      toastHelper.newMessage(
        'info',
        'Upgrade Required',
        'Bulk upload is available for Pro users only. Upgrade to Pro to upload multiple tracks at once.'
      );
      return;
    }
    const newId = nextIdRef.current++;
    setUploadContainers(prev => [
      ...prev,
      { 
        ...initialUploadContainer, 
        id: newId, 
        imageResize: { enabled: true, horizontal_offset: undefined, vertical_offset: undefined }, 
        uploadDate: null, 
        isScheduled: false, 
        playlistId: '', 
        madeForKids: 'no',
        disableWatermark: canDisableWatermark ? 'yes' : 'no'
      }
    ]);
  };

  const handleRemoveContainer = useCallback((index: number) => {
    setUploadContainers(prev => {
      if (prev.length <= 1) {
        return prev; // Always keep at least one track
      }
      const updated = prev.filter((_, i) => i !== index);
      setErrorsList(prevErrors => prevErrors.filter((_, i) => i !== index));
      return updated;
    });
  }, []);

  const handleUpdateContainer = useCallback(
    (index: number, updatedValues: Partial<UploadContainerData>) => {
      setUploadContainers(prev => {
        const updated = [...prev];
        let newValues = { ...updatedValues };
        
        // Enforce privacy when scheduled
        if (newValues.isScheduled) {
          newValues.privacyStatus = 'private';
        }
        
        updated[index] = { ...updated[index], ...newValues };

        if (hasSubmitted) {
          setErrorsList(prevErrors => {
            const errorsCopy = [...prevErrors];
            errorsCopy[index] = validateContainer(updated[index], false);
            return errorsCopy;
          });
        }

        return updated;
      });
    },
    [hasSubmitted]
  );

  const resetForm = () => {
    nextIdRef.current = 2;
    setUploadContainers([{ ...initialUploadContainer, id: 1, imageResize: null, uploadDate: null, isScheduled: false, playlistId: '', madeForKids: 'no', disableWatermark: canDisableWatermark ? 'yes' : 'no' }]);
    setErrorsList([]);
    setHasSubmitted(false);
    setResetSignal(prev => prev + 1);
  };

  // Helper function to check if any containers have portrait images without resize enabled
  const checkForPortraitImagesWithoutResize = async (): Promise<number[]> => {
    // Only warn if user has crop or auto-resize available to act on
    if (!canCropImage && !canAutoResizeImage) {
      return [];
    }

    const portraitChecks = await Promise.all(
      uploadContainers.map(async (container, index) => {
        if (!container.image || container.imageResize?.enabled) {
          return { index, isPortrait: false }; // No image or resize already enabled
        }

        return new Promise<{ index: number; isPortrait: boolean }>((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(container.image!);

          img.onload = () => {
            const isPortrait = img.width < img.height;
            URL.revokeObjectURL(url);
            resolve({ index, isPortrait });
          };

          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ index, isPortrait: false });
          };

          img.src = url;
        });
      })
    );

    // Return indices (1-based) of containers with portrait images
    return portraitChecks
      .filter(check => check.isPortrait)
      .map(check => check.index + 1); // Convert to 1-based indexing for display
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);

    const errors = uploadContainers.map(container =>
      validateContainer(container, false)
    );
    const hasErrors = errors.some(err => Object.keys(err).length > 0);
    setErrorsList(errors);

    if (hasErrors) {
      toastHelper.newMessage('error', 'Validation Error', 'Please fix the form errors before submitting.');
      return;
    }

    // Check for portrait images without resize
    const portraitIndices = await checkForPortraitImagesWithoutResize();
    if (portraitIndices.length > 0) {
      setPortraitImageIndices(portraitIndices);
      setShowPortraitWarningModal(true);
      return; // Wait for user confirmation
    }

    proceedWithUpload();
  };

  const proceedWithUpload = async () => {
    setIsUploading(true);

    try {
      const tunes = await Promise.all(
        uploadContainers.map(async (c) => ({
          video_title: c.title,
          video_description: c.description || '',
          tags: c.tags || [],
          category: c.category,
          playlist_id: c.playlistId || null,
          privacy_status: c.privacyStatus,
          license: c.license,
          made_for_kids: c.madeForKids === 'yes',
          disable_watermark: c.disableWatermark === 'yes',
          img_file_base64: c.image ? await fileConverter.fileToBase64(c.image) : null,
          audio_file_base64: c.audio ? await fileConverter.fileToBase64(c.audio) : null,
          scheduled: c.isScheduled,
          upload_date: (c.uploadDate ?? new Date()).toISOString(),
          image_resize: c.imageResize ? {
            enabled: c.imageResize.enabled,
            horizontal_offset: c.imageResize.horizontal_offset,
            vertical_offset: c.imageResize.vertical_offset,
          } : undefined,
        }))
      );

      const response = await createTuneUpload(tunes);

      toastHelper.newMessage('success', 'Success', 'Tunes uploaded successfully.');

      // Clear saved data after successful upload
      await clearUploadData();

      const links = (response.data || []).map((t: any) => ({
        video_title: t.video_title,
        youtube_link_watch: t.youtube_link_watch,
        benchmark_ffmpeg_process_time: t.benchmark_ffmpeg_process_time,
        benchmark_ffmpeg_upload_time: t.benchmark_ffmpeg_upload_time,
      }));

      setUploadedTuneLinks(links);
      setShowDialog(true);
      resetForm();
    } catch (error: any) {
      // Extract error message from API response
      const errorData = error.response?.data || {};
      const errorTitle = errorData.title || 'Upload Failed';
      
      // Check for timeout errors
      let errorMessage: string;
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. The image file may be too large. Please try a smaller image or check your internet connection.';
      } else {
        errorMessage = errorData.message || error.message || 'An unexpected error occurred.';
      }
      
      toastHelper.newMessage(
        'error',
        errorTitle,
        errorMessage
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setUploadedTuneLinks([]);
  };

  return (
    <>
      <div className="px-6 pt-6 pb-14 sm:pb-18 space-y-6 w-full flex flex-col items-center">
        <div className="space-y-4 w-full flex flex-col items-center">
          <UploadList
            uploadContainers={uploadContainers}
            onUpdate={handleUpdateContainer}
            onRemove={handleRemoveContainer}
            errorsList={errorsList}
            resetSignal={resetSignal}
          />
        </div>

        <div className="flex gap-2 flex-wrap justify-end w-full max-w-4xl">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={handleAddContainer}
                    disabled={isUploading || !canBulkUpload}
                    className={cn(
                      !canBulkUpload && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    Add Another Track
                  </Button>
                </span>
              </TooltipTrigger>
              {!canBulkUpload && (
                <TooltipContent>
                  <p>Upgrade to Pro to enable bulk upload</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          {!isAuthenticated ? (
            <>
              <div ref={googleButtonRef} className="hidden">
                <GoogleLogin
                  onSuccess={async (response) => {
                    // Save current upload data before login
                    console.log('[IndexedDB] Saving', uploadContainers.length, 'containers before OAuth');
                    await saveUploadData(uploadContainers);
                    console.log('[IndexedDB] Data saved successfully');
                    // Set flag to indicate fresh login (will be checked after page reload)
                    sessionStorage.setItem('justLoggedIn', 'true');
                    onLogin(response);
                  }}
                  onError={() => {
                    console.error('Login failed');
                    toastHelper.newMessage('error', 'Login Failed', 'Please try again');
                  }}
                  useOneTap={false}
                />
              </div>
              <Button
                onClick={() => {
                  // Trigger the hidden Google Login button
                  const googleBtn = googleButtonRef.current?.querySelector('div[role="button"]') as HTMLElement;
                  if (googleBtn) {
                    googleBtn.click();
                  } else {
                    console.error('[Google Login] Button not found in DOM');
                  }
                }}
                className="bg-card hover:bg-card/90 text-card-foreground border border-input transition-all duration-300 flex items-center gap-2 shadow-lg shadow-purple-500/20"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" fillRule="evenodd">
                    <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </g>
                </svg>
                Sign in with Google
              </Button>
            </>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isUploading}
              className={cn(
                "transition-all duration-300",
                uploadContainers.some(c => c.isScheduled)
                  ? "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  : "bg-primary hover:bg-primary/90 text-primary-foreground",
              )}
            >
              Submit Uploads
            </Button>
          )}
        </div>

        {isUploading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Uploading...</span>
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Uploaded Tunes</DialogTitle>
              <DialogDescription>
                Your tunes have been successfully uploaded to YouTube.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {uploadedTuneLinks.map((tune, idx) => (
                <Card key={idx} className="shadow-primary-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">{tune.video_title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-sm">
                      <strong>Video Processing Time:</strong> {tune.benchmark_ffmpeg_process_time}s
                    </div>
                    <div className="text-sm">
                      <strong>Upload Time:</strong> {tune.benchmark_ffmpeg_upload_time}s
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <a
                        href={tune.youtube_link_watch}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        Watch on YouTube
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigator.clipboard.writeText(tune.youtube_link_watch)}
                        className="h-7 px-3 text-xs"
                      >
                        Copy Link
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={handleDialogClose}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Portrait Image Warning Modal */}
        <Dialog open={showPortraitWarningModal} onOpenChange={setShowPortraitWarningModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Portrait Image Detected</DialogTitle>
              <DialogDescription>
                {portraitImageIndices.length === 1
                  ? `Upload #${portraitImageIndices[0]} has a portrait (vertical) image without Crop Image or Auto-Resize enabled.`
                  : `Uploads #${portraitImageIndices.join(', #')} have portrait (vertical) images without Crop Image or Auto-Resize enabled.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                YouTube may automatically classify portrait videos as <strong>YouTube Shorts</strong>, which may not be what you want.
              </p>
              <p className="text-sm text-muted-foreground">
                We recommend using the <strong>"Crop Image"</strong> or <strong>"Auto-Resize"</strong> feature to convert {portraitImageIndices.length === 1 ? 'this image' : 'these images'} to landscape format (1920x1080).
              </p>
              <p className="text-sm font-semibold text-foreground">
                Are you sure you want to continue without resizing?
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowPortraitWarningModal(false)}
              >
                Go Back
              </Button>
              <Button
                onClick={() => {
                  setShowPortraitWarningModal(false);
                  proceedWithUpload();
                }}
              >
                Continue Anyway
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <TermsBanner />
    </>
  );
}

export default TuneUpload;
